import { parseSseStream } from '@govard/llm-gate-client';

import { ApiError, apiGet, apiPost } from '../../../shared/api-client/index.js';

import type { ToolCall, Usage } from '@govard/ai-chat-kit';

export type ChatContextEntityType = 'deal' | 'contact' | 'none';
export type ChatMessageRole = 'user' | 'assistant' | 'tool' | 'system';

export interface ChatMessageItem {
  id: string;
  role: ChatMessageRole;
  content: string;
  toolCalls?: ToolCall[] | null;
  toolCallId?: string | null;
  createdAt: string;
}

export interface ChatConversationItem {
  id: string;
  workspaceId: string;
  userId: string;
  contextEntityType: ChatContextEntityType;
  contextEntityId: string;
}

export interface PostChatMessageInput {
  message: string;
  contextEntityType: ChatContextEntityType;
  contextEntityId: string;
}

export interface ChatTurnResponse {
  conversation: ChatConversationItem;
  messages: ChatMessageItem[];
  usage: Usage;
  traceId: string;
}

export interface ChatConversationResponse {
  conversation: ChatConversationItem;
  messages: ChatMessageItem[];
}

export type ChatStreamEvent =
  | {
      type: 'assistant_delta';
      content: string;
    }
  | {
      type: 'done';
      messages: ChatMessageItem[];
      usage: Usage;
      traceId: string;
    };

async function parseStreamError(res: Response): Promise<ApiError> {
  let payload: { message?: string; error?: string; code?: string; details?: unknown } = {};

  try {
    payload = (await res.json()) as { message?: string; error?: string; code?: string; details?: unknown };
  } catch {
    // Ignore parse failures and fall back to HTTP status text.
  }

  const message = payload.message ?? payload.error ?? `HTTP ${res.status}`;
  return new ApiError(res.status, message, payload.code, payload.details);
}

export async function getChatConversation(conversationId: string): Promise<ChatConversationResponse> {
  return apiGet<ChatConversationResponse>(`/chat/${conversationId}`);
}

export async function postChatMessage(
  conversationId: string,
  input: PostChatMessageInput,
): Promise<ChatTurnResponse> {
  return apiPost<ChatTurnResponse, PostChatMessageInput>(`/chat/${conversationId}/message`, input);
}

export async function* postChatMessageStream(
  conversationId: string,
  input: PostChatMessageInput,
  options: { signal?: AbortSignal } = {},
): AsyncGenerator<ChatStreamEvent, void, void> {
  const res = await fetch(`/api/v1/chat/${conversationId}/message`, {
    method: 'POST',
    credentials: 'include',
    ...(options.signal ? { signal: options.signal } : {}),
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!res.ok) {
    throw await parseStreamError(res);
  }

  if (!res.body) {
    throw new ApiError(500, 'Streaming response has no body', 'empty_stream_body');
  }

  for await (const event of parseSseStream(res.body)) {
    if (event.data === '[DONE]') {
      return;
    }

    yield JSON.parse(event.data) as ChatStreamEvent;
  }
}
